import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { FiArrowLeft, FiCheck, FiCreditCard, FiAlertCircle, FiPlus } from 'react-icons/fi';
import Layout from '../components/Layout';
import theme from '../styles/theme';
import { meusCartoesAPI, cartoesCreditoAPI, clientesAPI, planosAPI } from '../services/api';
import toast from 'react-hot-toast';
import CreditCardBrandIcon from '../components/CreditCardBrandIcon';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
  max-width: 800px;
  margin: 0 auto;
  padding: ${theme.spacing[6]};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${theme.borderRadius.full};
  border: 1px solid ${theme.colors.border.primary};
  background: ${theme.colors.background.secondary};
  color: ${theme.colors.text.primary};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${theme.colors.primary[500]};
    color: ${theme.colors.primary[500]};
    background: ${theme.colors.primary[50]};
  }
`;

const HeaderContent = styled.div`
  flex: 1;
`;

const Title = styled.h1`
  font-size: ${theme.fontSizes['3xl']};
  font-weight: 700;
  color: ${theme.colors.text.primary};
  margin: 0 0 ${theme.spacing[1]} 0;
`;

const Subtitle = styled.p`
  font-size: ${theme.fontSizes.base};
  color: ${theme.colors.text.secondary};
  margin: 0;
`;

const PlanoInfoBox = styled.div`
  padding: ${theme.spacing[4]};
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  margin-bottom: ${theme.spacing[6]};
`;

const PlanoInfoTitle = styled.h3`
  font-size: ${theme.fontSizes.lg};
  font-weight: 600;
  color: ${theme.colors.text.primary};
  margin: 0 0 ${theme.spacing[3]} 0;
`;

const PlanoInfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing[4]};
`;

const PlanoInfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};
`;

const PlanoInfoLabel = styled.span`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.secondary};
  font-weight: 500;
`;

const PlanoInfoValue = styled.span`
  font-size: ${theme.fontSizes.base};
  color: ${theme.colors.text.primary};
  font-weight: 600;
`;

const CardSelectionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
  margin-bottom: ${theme.spacing[6]};
`;

const CardOption = styled.div`
  padding: ${theme.spacing[4]};
  border: 2px solid ${props => props.$selected ? theme.colors.primary[500] : theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  background: ${props => props.$selected ? theme.colors.primary[600] : theme.colors.background.primary};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};

  &:hover {
    border-color: ${props => props.$selected ? theme.colors.primary[500] : theme.colors.primary[300]};
    background: ${props => props.$selected ? theme.colors.primary[600] : theme.colors.primary[50]};
  }
`;

const CardOptionContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};
`;

const CardOptionNumber = styled.div`
  font-size: ${theme.fontSizes.lg};
  font-weight: 600;
  color: ${props => props.$selected ? '#ffffff' : theme.colors.text.primary};
`;

const CardOptionDetails = styled.div`
  font-size: ${theme.fontSizes.sm};
  color: ${props => props.$selected ? 'rgba(255, 255, 255, 0.9)' : theme.colors.text.secondary};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const CardOptionBadge = styled.span`
  padding: ${theme.spacing[1]} ${theme.spacing[2]};
  background: ${theme.colors.success[500]};
  color: white;
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.fontSizes.xs};
  font-weight: 600;
`;

const AddCardButton = styled.button`
  padding: ${theme.spacing[4]};
  border: 2px dashed ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  background: transparent;
  color: ${theme.colors.text.secondary};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};
  font-weight: 600;
  margin-bottom: ${theme.spacing[6]};

  &:hover {
    border-color: ${theme.colors.primary[500]};
    color: ${theme.colors.primary[600]};
    background: ${theme.colors.primary[50]};
  }
`;

const Actions = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
  justify-content: flex-end;
  padding-top: ${theme.spacing[4]};
  border-top: 1px solid ${theme.colors.border.primary};
`;

const ConfirmButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  background: ${props => props.$disabled ? theme.colors.gray[400] : theme.colors.primary[500]};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.xl};
  font-weight: 600;
  font-size: ${theme.fontSizes.base};
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover:not(:disabled) {
    background: ${theme.colors.primary[600]};
  }
`;

const CancelButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  background: transparent;
  color: ${theme.colors.text.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  font-weight: 600;
  font-size: ${theme.fontSizes.base};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${theme.colors.error[500]};
    color: ${theme.colors.error[600]};
    background: ${theme.colors.error[50]};
  }
`;

const AlertBox = styled.div`
  padding: ${theme.spacing[4]};
  background: ${theme.colors.warning[50]};
  border: 1px solid ${theme.colors.warning[300]};
  border-radius: ${theme.borderRadius.xl};
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[3]};
  margin-bottom: ${theme.spacing[4]};
  color: ${theme.colors.warning[800]};

  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }

  strong {
    display: block;
    margin-bottom: ${theme.spacing[1]};
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  font-size: ${theme.fontSizes.lg};
  color: ${theme.colors.text.muted};
`;

const SelecionarCartaoUpgrade = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clienteUuidParam = searchParams.get('cliente_uuid');

  const [loading, setLoading] = useState(true);
  const [cartoes, setCartoes] = useState([]);
  const [cartaoSelecionado, setCartaoSelecionado] = useState(null);
  const [upgrading, setUpgrading] = useState(false);
  const [clienteId, setClienteId] = useState(null);
  const [planoSelecionado, setPlanoSelecionado] = useState(null);
  const [planosDisponiveis, setPlanosDisponiveis] = useState([]);

  useEffect(() => {
    let isMounted = true;
    let cancelled = false;
    
    const loadData = async () => {
      try {
        console.log('[SelecionarCartaoUpgrade] useEffect iniciado', { clienteUuidParam, isMounted });
        
        if (cancelled || !isMounted) {
          console.log('[SelecionarCartaoUpgrade] Carregamento cancelado');
          return;
        }
        
        await carregarDados();
        
        if (cancelled || !isMounted) {
          console.log('[SelecionarCartaoUpgrade] Carregamento concluído mas componente desmontado');
          return;
        }
        
        console.log('[SelecionarCartaoUpgrade] Carregamento concluído com sucesso');
      } catch (error) {
        console.error('[SelecionarCartaoUpgrade] Erro no useEffect:', {
          error: error.message,
          stack: error.stack,
          name: error.name,
          cancelled,
          isMounted
        });
        
        if (isMounted && !cancelled) {
          try {
            toast.error('Erro ao carregar dados');
            sessionStorage.removeItem('upgrade_plano_id');
            setTimeout(() => navigate('/upgrade-plano'), 100);
          } catch (navigationError) {
            console.error('[SelecionarCartaoUpgrade] Erro ao navegar após erro:', navigationError);
            sessionStorage.removeItem('upgrade_plano_id');
            window.location.href = '/upgrade-plano';
          }
        }
      }
    };

    // Garantir que loadData seja executado de forma segura
    const promise = loadData();
    
    // Tratar a promise explicitamente
    promise.catch((error) => {
      if (!cancelled && isMounted) {
        console.error('[SelecionarCartaoUpgrade] Promise rejeitada no useEffect:', error);
      }
    });

    return () => {
      console.log('[SelecionarCartaoUpgrade] useEffect cleanup');
      cancelled = true;
      isMounted = false;
    };
  }, [clienteUuidParam]);

  const carregarDados = async () => {
    try {
      console.log('[SelecionarCartaoUpgrade] Iniciando carregamento de dados', { clienteUuidParam });
      
      // Garantir que setLoading seja chamado de forma segura
      try {
        setLoading(true);
      } catch (setStateError) {
        console.error('[SelecionarCartaoUpgrade] Erro ao definir loading:', setStateError);
        throw setStateError;
      }

      if (!clienteUuidParam) {
        console.error('[SelecionarCartaoUpgrade] Cliente UUID não fornecido');
        try {
          toast.error('Cliente não identificado');
          setTimeout(() => navigate('/upgrade-plano'), 100);
        } catch (navError) {
          console.error('[SelecionarCartaoUpgrade] Erro ao navegar:', navError);
          window.location.href = '/upgrade-plano';
        }
        return;
      }

      // Buscar ID do cliente pelo UUID usando a API
      console.log('[SelecionarCartaoUpgrade] Buscando cliente por UUID:', clienteUuidParam);
      let clienteResponse;
      try {
        clienteResponse = await clientesAPI.buscarPorId(clienteUuidParam);
        console.log('[SelecionarCartaoUpgrade] Resposta do cliente:', { 
          success: clienteResponse?.data?.success,
          hasData: !!clienteResponse?.data?.data 
        });
      } catch (clienteError) {
        console.error('[SelecionarCartaoUpgrade] Erro ao buscar cliente:', clienteError);
        try {
          toast.error('Erro ao buscar dados do cliente');
          sessionStorage.removeItem('upgrade_plano_id');
          setTimeout(() => navigate('/upgrade-plano'), 100);
        } catch (navError) {
          console.error('[SelecionarCartaoUpgrade] Erro ao navegar após erro cliente:', navError);
          sessionStorage.removeItem('upgrade_plano_id');
          window.location.href = '/upgrade-plano';
        }
        return;
      }

      if (!clienteResponse?.data?.success || !clienteResponse?.data?.data) {
        console.error('[SelecionarCartaoUpgrade] Resposta inválida do cliente:', clienteResponse);
        try {
          toast.error('Erro ao buscar dados do cliente');
          sessionStorage.removeItem('upgrade_plano_id');
          setTimeout(() => navigate('/upgrade-plano'), 100);
        } catch (navError) {
          console.error('[SelecionarCartaoUpgrade] Erro ao navegar após resposta inválida:', navError);
          sessionStorage.removeItem('upgrade_plano_id');
          window.location.href = '/upgrade-plano';
        }
        return;
      }

      const clienteIdNum = clienteResponse.data.data.id;
      console.log('[SelecionarCartaoUpgrade] Cliente encontrado:', { clienteId: clienteIdNum });
      
      try {
        setClienteId(clienteIdNum);
      } catch (setStateError) {
        console.error('[SelecionarCartaoUpgrade] Erro ao definir clienteId:', setStateError);
        throw setStateError;
      }

      // Buscar planos disponíveis para upgrade (validação de segurança)
      console.log('[SelecionarCartaoUpgrade] Buscando planos disponíveis para upgrade');
      let planosResponse;
      try {
        planosResponse = await planosAPI.listarPlanosUpgrade(null, clienteUuidParam);
        console.log('[SelecionarCartaoUpgrade] Resposta dos planos:', { 
          success: planosResponse?.data?.success,
          planosCount: planosResponse?.data?.data?.planosDisponiveis?.length || 0
        });
      } catch (planosError) {
        console.error('[SelecionarCartaoUpgrade] Erro ao buscar planos:', planosError);
        try {
          toast.error('Erro ao buscar planos disponíveis');
          sessionStorage.removeItem('upgrade_plano_id');
          setTimeout(() => navigate('/upgrade-plano'), 100);
        } catch (navError) {
          console.error('[SelecionarCartaoUpgrade] Erro ao navegar após erro planos:', navError);
          sessionStorage.removeItem('upgrade_plano_id');
          window.location.href = '/upgrade-plano';
        }
        return;
      }

      if (!planosResponse || !planosResponse.data?.success) {
        try {
          toast.error('Erro ao buscar planos disponíveis');
          sessionStorage.removeItem('upgrade_plano_id');
          setTimeout(() => navigate('/upgrade-plano'), 100);
        } catch (navError) {
          console.error('[SelecionarCartaoUpgrade] Erro ao navegar após resposta inválida planos:', navError);
          sessionStorage.removeItem('upgrade_plano_id');
          window.location.href = '/upgrade-plano';
        }
        return;
      }

      const planos = planosResponse.data.data?.planosDisponiveis || [];
      console.log('[SelecionarCartaoUpgrade] Planos disponíveis:', planos.length);
      
      try {
        setPlanosDisponiveis(planos);
      } catch (setStateError) {
        console.error('[SelecionarCartaoUpgrade] Erro ao definir planosDisponiveis:', setStateError);
        throw setStateError;
      }
      
      // Buscar plano_id do sessionStorage
      const planoIdFromStorage = sessionStorage.getItem('upgrade_plano_id');
      console.log('[SelecionarCartaoUpgrade] Plano ID do storage:', planoIdFromStorage);
      
      if (!planoIdFromStorage) {
        console.error('[SelecionarCartaoUpgrade] Plano ID não encontrado no storage');
        try {
          toast.error('Plano não selecionado');
          setTimeout(() => navigate('/upgrade-plano'), 100);
        } catch (navError) {
          console.error('[SelecionarCartaoUpgrade] Erro ao navegar:', navError);
          window.location.href = '/upgrade-plano';
        }
        return;
      }

      const planoIdNum = parseInt(planoIdFromStorage, 10);
      if (isNaN(planoIdNum)) {
        console.error('[SelecionarCartaoUpgrade] Plano ID inválido:', planoIdFromStorage);
        try {
          toast.error('ID do plano inválido');
          sessionStorage.removeItem('upgrade_plano_id');
          setTimeout(() => navigate('/upgrade-plano'), 100);
        } catch (navError) {
          console.error('[SelecionarCartaoUpgrade] Erro ao navegar:', navError);
          sessionStorage.removeItem('upgrade_plano_id');
          window.location.href = '/upgrade-plano';
        }
        return;
      }

      // Validar que o plano está na lista de planos disponíveis
      const planoValido = planos.find(p => p.id === planoIdNum);
      console.log('[SelecionarCartaoUpgrade] Validação do plano:', { 
        planoIdNum, 
        planoValido: !!planoValido,
        planosIds: planos.map(p => p.id)
      });
      
      if (!planoValido) {
        console.error('[SelecionarCartaoUpgrade] Plano não válido para upgrade:', planoIdNum);
        try {
          toast.error('Plano selecionado não é válido para upgrade');
          sessionStorage.removeItem('upgrade_plano_id');
          setTimeout(() => navigate('/upgrade-plano'), 100);
        } catch (navError) {
          console.error('[SelecionarCartaoUpgrade] Erro ao navegar:', navError);
          sessionStorage.removeItem('upgrade_plano_id');
          window.location.href = '/upgrade-plano';
        }
        return;
      }

      try {
        setPlanoSelecionado(planoIdNum);
        console.log('[SelecionarCartaoUpgrade] Plano selecionado definido:', planoIdNum);
      } catch (setStateError) {
        console.error('[SelecionarCartaoUpgrade] Erro ao definir planoSelecionado:', setStateError);
        throw setStateError;
      }

      // Buscar cartões do cliente específico
      console.log('[SelecionarCartaoUpgrade] Buscando cartões do cliente:', clienteIdNum);
      let response;
      try {
        response = await cartoesCreditoAPI.listarPorCliente(clienteIdNum);
        console.log('[SelecionarCartaoUpgrade] Resposta dos cartões:', { 
          success: response?.data?.success,
          cartoesCount: response?.data?.data?.length || 0
        });
      } catch (cartoesError) {
        console.error('[SelecionarCartaoUpgrade] Erro ao buscar cartões:', cartoesError);
        try {
          toast.error('Erro ao carregar cartões de crédito');
          sessionStorage.removeItem('upgrade_plano_id');
          setTimeout(() => navigate('/upgrade-plano'), 100);
        } catch (navError) {
          console.error('[SelecionarCartaoUpgrade] Erro ao navegar após erro cartões:', navError);
          sessionStorage.removeItem('upgrade_plano_id');
          window.location.href = '/upgrade-plano';
        }
        return;
      }

      if (!response || !response.data?.success) {
        try {
          toast.error('Erro ao carregar cartões de crédito');
          sessionStorage.removeItem('upgrade_plano_id');
        } catch (toastError) {
          console.error('[SelecionarCartaoUpgrade] Erro ao mostrar toast:', toastError);
        }
        return;
      }

      const cartoesAtivos = (response.data.data || []).filter(c => c.ativo);
      console.log('[SelecionarCartaoUpgrade] Cartões ativos filtrados:', cartoesAtivos.length);
      
      try {
        setCartoes(cartoesAtivos);

        // Selecionar cartão padrão automaticamente
        const cartaoPadrao = cartoesAtivos.find(c => c.padrao);
        if (cartaoPadrao) {
          console.log('[SelecionarCartaoUpgrade] Selecionando cartão padrão:', cartaoPadrao.id);
          setCartaoSelecionado(cartaoPadrao.id);
        } else if (cartoesAtivos.length > 0) {
          console.log('[SelecionarCartaoUpgrade] Selecionando primeiro cartão:', cartoesAtivos[0].id);
          setCartaoSelecionado(cartoesAtivos[0].id);
        } else {
          console.warn('[SelecionarCartaoUpgrade] Nenhum cartão ativo encontrado');
        }
      } catch (setStateError) {
        console.error('[SelecionarCartaoUpgrade] Erro ao definir estado dos cartões:', setStateError);
        throw setStateError;
      }
    } catch (error) {
      console.error('[SelecionarCartaoUpgrade] Erro inesperado ao carregar dados:', {
        error: error.message,
        stack: error.stack,
        name: error.name
      });
      try {
        toast.error('Erro ao carregar dados');
        sessionStorage.removeItem('upgrade_plano_id');
        setTimeout(() => navigate('/upgrade-plano'), 100);
      } catch (navError) {
        console.error('[SelecionarCartaoUpgrade] Erro ao navegar após erro inesperado:', navError);
        sessionStorage.removeItem('upgrade_plano_id');
        window.location.href = '/upgrade-plano';
      }
    } finally {
      try {
        setLoading(false);
        console.log('[SelecionarCartaoUpgrade] Carregamento finalizado');
      } catch (setStateError) {
        console.error('[SelecionarCartaoUpgrade] Erro ao definir loading false:', setStateError);
        // Não relançar o erro aqui, apenas logar
      }
    }
  };

  const handleConfirmarUpgrade = async () => {
    console.log('[SelecionarCartaoUpgrade] ========== INICIANDO CONFIRMAÇÃO DE UPGRADE ==========');
    console.log('[SelecionarCartaoUpgrade] Dados iniciais:', {
      planoSelecionado,
      cartaoSelecionado,
      clienteId,
      planosDisponiveisCount: planosDisponiveis.length,
      cartoesCount: cartoes.length
    });

    if (!planoSelecionado) {
      console.error('[SelecionarCartaoUpgrade] ❌ Plano não selecionado');
      toast.error('Plano não selecionado');
      return;
    }

    if (!cartaoSelecionado) {
      console.error('[SelecionarCartaoUpgrade] ❌ Cartão não selecionado');
      toast.error('Selecione um cartão de crédito para continuar');
      return;
    }

    if (!clienteId) {
      console.error('[SelecionarCartaoUpgrade] ❌ Cliente ID não definido');
      toast.error('Cliente não identificado');
      return;
    }

    try {
      console.log('[SelecionarCartaoUpgrade] 🔄 Definindo upgrading = true');
      setUpgrading(true);
      console.log('[SelecionarCartaoUpgrade] ✅ upgrading definido');
      
      // Validar novamente que o plano está na lista de planos disponíveis
      const planoValido = planosDisponiveis.find(p => p.id === planoSelecionado);
      console.log('[SelecionarCartaoUpgrade] 🔍 Validação final do plano:', {
        planoSelecionado,
        planoValido: !!planoValido,
        planosDisponiveis: planosDisponiveis.map(p => p.id),
        planoValidoDetalhes: planoValido ? { id: planoValido.id, nome: planoValido.nome } : null
      });
      
      if (!planoValido) {
        console.error('[SelecionarCartaoUpgrade] ❌ Plano não válido para upgrade');
        toast.error('Plano selecionado não é válido para upgrade');
        sessionStorage.removeItem('upgrade_plano_id');
        setUpgrading(false);
        navigate('/upgrade-plano');
        return;
      }

      console.log('[SelecionarCartaoUpgrade] 📡 Preparando chamada API...', {
        clienteId,
        planoId: planoSelecionado,
        cartaoId: cartaoSelecionado,
        endpoint: '/planos/upgrade',
        payload: { cliente_id: clienteId, plano_id: planoSelecionado, cartao_id: cartaoSelecionado }
      });

      const startTime = Date.now();
      console.log('[SelecionarCartaoUpgrade] ⏱️ Iniciando chamada API em:', new Date().toISOString());
      
      // Criar um timeout de segurança (2 minutos)
      const timeoutMs = 120000;
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error(`Timeout: A requisição demorou mais de ${timeoutMs}ms`));
        }, timeoutMs);
      });
      
      let response;
      try {
        // Usar Promise.race para garantir que não trave indefinidamente
        response = await Promise.race([
          planosAPI.realizarUpgrade(clienteId, planoSelecionado, cartaoSelecionado),
          timeoutPromise
        ]);
        
        const duration = Date.now() - startTime;
        console.log('[SelecionarCartaoUpgrade] ✅ Resposta recebida em', duration, 'ms');
        console.log('[SelecionarCartaoUpgrade] 📦 Resposta completa:', {
          status: response?.status,
          statusText: response?.statusText,
          success: response?.data?.success,
          error: response?.data?.error,
          data: response?.data?.data,
          headers: response?.headers
        });
      } catch (apiError) {
        const duration = Date.now() - startTime;
        console.error('[SelecionarCartaoUpgrade] ❌ Erro na chamada API após', duration, 'ms:', {
          message: apiError?.message,
          code: apiError?.code,
          status: apiError?.response?.status,
          statusText: apiError?.response?.statusText,
          data: apiError?.response?.data,
          stack: apiError?.stack,
          isTimeout: apiError?.message?.includes('Timeout')
        });
        
        // Se for timeout, mostrar mensagem específica
        if (apiError?.message?.includes('Timeout')) {
          toast.error('A requisição demorou muito tempo. Por favor, tente novamente.');
        }
        
        throw apiError;
      }
      
      // Limpar sessionStorage após tentativa
      console.log('[SelecionarCartaoUpgrade] 🧹 Limpando sessionStorage');
      sessionStorage.removeItem('upgrade_plano_id');

      if (response?.data?.success) {
        console.log('[SelecionarCartaoUpgrade] ✅ Upgrade realizado com sucesso!');
        console.log('[SelecionarCartaoUpgrade] 📊 Dados do upgrade:', response?.data?.data);
        
        toast.success('Upgrade realizado com sucesso!');
        
        console.log('[SelecionarCartaoUpgrade] 🧭 Navegando para /dashboard/creditos');
        try {
          navigate('/dashboard/creditos');
          console.log('[SelecionarCartaoUpgrade] ✅ Navegação iniciada');
        } catch (navError) {
          console.error('[SelecionarCartaoUpgrade] ❌ Erro ao navegar:', navError);
          // Fallback para window.location se navigate falhar
          window.location.href = '/dashboard/creditos';
        }
      } else {
        console.error('[SelecionarCartaoUpgrade] ❌ Erro na resposta do upgrade:', {
          success: response?.data?.success,
          error: response?.data?.error,
          data: response?.data
        });
        const errorMessage = response?.data?.error || response?.data?.message || 'Erro ao realizar upgrade';
        toast.error(errorMessage);
        setUpgrading(false);
      }
    } catch (error) {
      console.error('[SelecionarCartaoUpgrade] ❌❌❌ ERRO CAPTURADO NO CATCH:', {
        name: error?.name,
        message: error?.message,
        stack: error?.stack,
        response: error?.response ? {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
          headers: error.response.headers
        } : null,
        config: error?.config ? {
          url: error.config.url,
          method: error.config.method,
          data: error.config.data,
          headers: error.config.headers
        } : null
      });
      
      const errorMessage = error?.response?.data?.error || 
                          error?.response?.data?.message || 
                          error?.message || 
                          'Erro ao realizar upgrade';
      
      console.log('[SelecionarCartaoUpgrade] 📢 Exibindo toast de erro:', errorMessage);
      toast.error(errorMessage);
      
      console.log('[SelecionarCartaoUpgrade] 🧹 Limpando sessionStorage após erro');
      sessionStorage.removeItem('upgrade_plano_id');
      
      setUpgrading(false);
    } finally {
      console.log('[SelecionarCartaoUpgrade] 🔄 Definindo upgrading = false no finally');
      setUpgrading(false);
      console.log('[SelecionarCartaoUpgrade] ========== PROCESSO DE UPGRADE FINALIZADO ==========');
    }
  };

  const formatarNumeroCartao = (lastFour) => {
    if (!lastFour) return '•••• •••• •••• ••••';
    return `•••• •••• •••• ${lastFour}`;
  };

  const formatarValidade = (mes, ano) => {
    if (!mes || !ano) return '—';
    const mesFormatado = String(mes).padStart(2, '0');
    const anoFormatado = String(ano).slice(-2);
    return `${mesFormatado}/${anoFormatado}`;
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
    }).format(value);
  };

  // Buscar dados completos do plano selecionado
  const planoCompleto = planosDisponiveis.find(p => p.id === planoSelecionado);

  if (loading) {
    return (
      <Layout>
        <Container>
          <LoadingSpinner>Carregando cartões...</LoadingSpinner>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container>
        <Header>
          <BackButton onClick={() => {
            sessionStorage.removeItem('upgrade_plano_id');
            navigate(-1);
          }}>
            <FiArrowLeft />
          </BackButton>
          <HeaderContent>
            <Title>Selecione o Cartão de Crédito</Title>
            <Subtitle>Escolha o cartão de crédito que será usado para as futuras cobranças do novo plano.</Subtitle>
          </HeaderContent>
        </Header>

        {/* Informações do plano selecionado */}
        {planoCompleto && (
          <PlanoInfoBox>
            <PlanoInfoTitle>Plano Selecionado</PlanoInfoTitle>
            <PlanoInfoGrid>
              <PlanoInfoItem>
                <PlanoInfoLabel>Nome do Plano</PlanoInfoLabel>
                <PlanoInfoValue>{planoCompleto.nome}</PlanoInfoValue>
              </PlanoInfoItem>
              <PlanoInfoItem>
                <PlanoInfoLabel>Valor Mensal</PlanoInfoLabel>
                <PlanoInfoValue>{formatCurrency(planoCompleto.valor)}</PlanoInfoValue>
              </PlanoInfoItem>
              <PlanoInfoItem>
                <PlanoInfoLabel>Créditos Mensais</PlanoInfoLabel>
                <PlanoInfoValue>
                  {planoCompleto.creditos_mensais?.toLocaleString('pt-BR') || 0} créditos
                </PlanoInfoValue>
              </PlanoInfoItem>
            </PlanoInfoGrid>
          </PlanoInfoBox>
        )}

        {cartoes.length === 0 ? (
          <AlertBox>
            <FiAlertCircle size={24} />
            <div>
              <strong>Nenhum cartão cadastrado</strong>
              <p>Você precisa cadastrar um cartão de crédito antes de fazer upgrade.</p>
            </div>
          </AlertBox>
        ) : (
          <CardSelectionList>
            {cartoes.map((cartao) => (
              <CardOption
                key={cartao.id}
                $selected={cartaoSelecionado === cartao.id}
                onClick={() => setCartaoSelecionado(cartao.id)}
              >
                <CreditCardBrandIcon brand={cartao.bandeira} size="48px" />
                <CardOptionContent>
                  <CardOptionNumber $selected={cartaoSelecionado === cartao.id}>{formatarNumeroCartao(cartao.number_last_four)}</CardOptionNumber>
                  <CardOptionDetails $selected={cartaoSelecionado === cartao.id}>
                    <span>{cartao.holder_name}</span>
                    <span>•</span>
                    <span>Validade: {formatarValidade(cartao.expiry_month, cartao.expiry_year)}</span>
                    {cartao.padrao && (
                      <>
                        <span>•</span>
                        <CardOptionBadge>Principal</CardOptionBadge>
                      </>
                    )}
                  </CardOptionDetails>
                </CardOptionContent>
                {cartaoSelecionado === cartao.id && (
                  <FiCheck size={24} color={theme.colors.primary[500]} />
                )}
              </CardOption>
            ))}
          </CardSelectionList>
        )}

        <AddCardButton onClick={() => {
          navigate(`/meus-cartoes/novo${clienteUuidParam ? `?cliente_uuid=${clienteUuidParam}` : ''}`);
        }}>
          <FiPlus size={20} />
          Adicionar Novo Cartão
        </AddCardButton>

        <Actions>
          <CancelButton onClick={() => {
            sessionStorage.removeItem('upgrade_plano_id');
            navigate(-1);
          }}>Cancelar</CancelButton>
          <ConfirmButton
            $disabled={!cartaoSelecionado || cartoes.length === 0 || upgrading}
            onClick={handleConfirmarUpgrade}
            disabled={!cartaoSelecionado || cartoes.length === 0 || upgrading}
          >
            {upgrading ? 'Processando...' : 'Confirmar Upgrade'}
          </ConfirmButton>
        </Actions>
      </Container>
    </Layout>
  );
};

export default SelecionarCartaoUpgrade;

